/* ============================================================================
   KOTT Lab — Hero · dot system
   ----------------------------------------------------------------------------
   The dot is the hero's design language, and this module owns the choreography
   around it:

     entrance  headline words rise out of their masks one by one, the dot lands
               and pulses, the lede follows, then the three service dots are
               released to fall and bounce in (physics in the hl-drop
               keyframes). Chained off `kott:loader:done`.
     hover     the headline dot resolves from its tri-colour orbit to the
               hovered service's single colour. The arrow's own animation is
               pure CSS (hl-arr-loop).
     click     a ripple spawns at the dot. Scrolling stays with hero-liquid.js,
               which already owns [data-hero-jump].
     cursor    a dot-shaped follower magnetically pulled to a service dot.

   Under reduced motion (OS preference or the accessibility widget) nothing
   arms and everything tears down to a static, fully visible state — see
   docs/accessibility-guidelines.md.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('.hero-liquid');
  if (!root) return;

  var titleDot = root.querySelector('.title .dot');
  var svcs = [].slice.call(root.querySelectorAll('.hl-svc'));
  var cursor = root.querySelector('.hl-cursor');
  if (!svcs.length) return;

  function isReduced() {
    return window.kottReduceMotion
      ? window.kottReduceMotion()
      : (window.matchMedia &&
         window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function isCoarse() {
    return window.matchMedia &&
      (window.matchMedia('(pointer: coarse)').matches ||
       window.matchMedia('(hover: none)').matches);
  }
  /* ------------------------------------------------------------- entrance */
  var armed = false, played = false;

  function arm() {
    if (armed || played || isReduced()) return;
    // The inline script in the section already armed this during parse; adding
    // it here too keeps the module correct if that script was stripped.
    root.classList.add('is-intro-armed', 'is-dots-armed');
    armed = true;
  }

  function play() {
    if (played) return;
    played = true;
    if (isReduced() || !armed) {
      root.classList.remove('is-intro-armed', 'is-dots-armed');
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-intro-in');
        // the dot lands at ~400ms; pulse it as it emits, then release the dots
        setTimeout(function () {
          if (titleDot) {
            titleDot.classList.add('is-emitting');
            setTimeout(function () { titleDot.classList.remove('is-emitting'); }, 950);
          }
        }, 620);
        setTimeout(function () { root.classList.add('is-dots-in'); }, 700);
      });
    });
  }

  if (!isReduced()) {
    arm();
    if (window.__kottLoaderDone) {
      setTimeout(play, 60);
    } else {
      document.addEventListener('kott:loader:done', function () { setTimeout(play, 60); }, { once: true });
      // loader skipped (repeat visit / design mode / failure) — never strand it
      setTimeout(play, 2600);
    }
  }

  /* ------------------------- headline dot resolves to the hovered service */
  function resolveTo(accent) {
    if (!titleDot || !accent) return;
    titleDot.style.setProperty('--hl-resolve', accent);
    titleDot.classList.add('is-resolved');
  }
  function unresolve() {
    if (titleDot) titleDot.classList.remove('is-resolved');
  }

  /* The arrow animation is pure CSS now (see hl-arr-loop): a WAAPI pair with
     fill:forwards stranded the two SVGs mid-swap across enter/leave cycles,
     leaving the disc empty. */

  svcs.forEach(function (el) {
    var accent = (el.style.getPropertyValue('--hl-svc') || '').trim();
    function on() { if (!isReduced()) resolveTo(accent); }
    function off() { unresolve(); }
    el.addEventListener('pointerenter', on, { passive: true });
    el.addEventListener('pointerleave', off, { passive: true });
    el.addEventListener('focus', on);
    el.addEventListener('blur', off);
  });

  /* --------------------------------------------------------- click ripple */
  svcs.forEach(function (el) {
    el.addEventListener('click', function () {
      if (isReduced()) return;
      var d = el.querySelector('.hl-svc__dot');
      if (!d) return;
      var er = el.getBoundingClientRect(), dr = d.getBoundingClientRect();
      var rip = document.createElement('span');
      rip.className = 'hl-ripple';
      rip.style.left = (dr.left - er.left + dr.width / 2) + 'px';
      rip.style.top = (dr.top - er.top + dr.height / 2) + 'px';
      el.appendChild(rip);
      setTimeout(function () { try { rip.remove(); } catch (e) {} }, 700);
    }, { passive: true });
  });

  /* --------------------------------------------------------------- cursor */
  var craf = 0, cOn = false;
  var pt = { x: -100, y: -100 }, pos = { x: -100, y: -100 }, scale = 0.6;

  function cursorLoop() {
    craf = requestAnimationFrame(cursorLoop);
    var tx = pt.x, ty = pt.y, ts = 0.6, near = null, best = 1e9;
    svcs.forEach(function (el) {
      var d = el.querySelector('.hl-svc__dot');
      if (!d) return;
      var r = d.getBoundingClientRect();
      var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      var dist = Math.hypot(cx - pt.x, cy - pt.y);
      if (dist < 90 && dist < best) { best = dist; near = { x: cx, y: cy, el: el }; }
    });
    if (near) {
      tx = near.x; ty = near.y; ts = 1.5;
      var acc = (near.el.style.getPropertyValue('--hl-svc') || '').trim();
      if (acc) cursor.style.setProperty('--hl-cursor-c', acc);
    } else {
      cursor.style.setProperty('--hl-cursor-c', '#576FF6');
    }
    pos.x += (tx - pos.x) * 0.18;
    pos.y += (ty - pos.y) * 0.18;
    scale += (ts - scale) * 0.16;
    cursor.style.transform =
      'translate3d(' + (pos.x - 13).toFixed(1) + 'px,' + (pos.y - 13).toFixed(1) + 'px,0) scale(' + scale.toFixed(3) + ')';
  }
  function cursorOn() {
    if (cOn || !cursor || isReduced() || isCoarse()) return;
    cOn = true; cursor.classList.add('is-on');
    if (!craf) craf = requestAnimationFrame(cursorLoop);
  }
  function cursorOff() {
    cOn = false;
    if (cursor) cursor.classList.remove('is-on');
    if (craf) { cancelAnimationFrame(craf); craf = 0; }
  }
  if (cursor && !isCoarse()) {
    root.addEventListener('pointerenter', cursorOn, { passive: true });
    root.addEventListener('pointerleave', cursorOff, { passive: true });
    root.addEventListener('pointermove', function (e) {
      pt.x = e.clientX; pt.y = e.clientY;
      if (!cOn) { pos.x = pt.x; pos.y = pt.y; cursorOn(); }
    }, { passive: true });
  }

  /* --------------------------------------------- reduced-motion contract */
  function teardown() {
    cursorOff();
    played = true;
    root.classList.remove('is-intro-armed', 'is-intro-in', 'is-dots-armed', 'is-dots-in');
    unresolve();
    if (titleDot) titleDot.classList.remove('is-emitting');
    [].slice.call(root.querySelectorAll('.hl-ripple')).forEach(function (r) {
      try { r.remove(); } catch (e) {}
    });
  }
  function onReduceChange(e) {
    if (e && e.detail && e.detail.reduced) teardown();
    else window.location.reload();
  }
  if (window.__hlDotsReduceHandler) {
    window.removeEventListener('kott:reduce-motion-change', window.__hlDotsReduceHandler);
  }
  window.__hlDotsReduceHandler = onReduceChange;
  window.addEventListener('kott:reduce-motion-change', onReduceChange);

  if (isReduced()) teardown();
})();
